import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { formatTimeRange, formatCurrency, getStatusBadgeClass } from '../../utils/formatters';
import { Calendar, DollarSign, AlertCircle, Clock, ChevronRight } from 'lucide-react';

// Format date relative to today, using venue timezone
function formatDateInTimezone(dateStr, timezone = 'America/Los_Angeles') {
  // Parse the date string as a local date (ignore time portion)
  const datePart = dateStr.substring(0, 10); // "2026-01-15"
  const [year, month, day] = datePart.split('-').map(Number);
  const gigDate = new Date(year, month - 1, day);
  
  // Get today in the venue's timezone
  const now = new Date();
  const todayStr = now.toLocaleDateString('en-US', { timeZone: timezone });
  const [m, d, y] = todayStr.split('/').map(Number);
  const today = new Date(y, m - 1, d);
  
  // Calculate difference in days
  const diffTime = gigDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  
  // Return formatted date
  return gigDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function GigCard({ gig, showActions = false, onAccept, onDecline }) {
  const navigate = useNavigate();
  
  const handleCardClick = () => {
    navigate(`/my/gig/${gig.id}`);
  };

  return (
    <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={handleCardClick}>
      <div className="card-body">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-sm text-gray-500">{formatDateInTimezone(gig.date, gig.timezone)}</p>
            <h3 className="font-medium">{gig.venue} - {gig.location}</h3>
          </div>
          <span className={getStatusBadgeClass(gig.status)}>{gig.status}</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatTimeRange(gig.startTime, gig.endTime)}</span>
          <span className="flex items-center gap-1 font-semibold text-green-600"><DollarSign className="w-4 h-4" />{formatCurrency(gig.payout)}</span>
        </div>
        {gig.role && <p className="text-sm text-gray-500 mt-1">Role: {gig.role}</p>}
        
        {showActions && gig.status === 'PENDING' && (
          <div className="flex gap-2 mt-3 pt-3 border-t" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => onAccept(gig.assignmentId)} className="btn-primary flex-1">Accept</button>
            <button onClick={() => onDecline(gig.assignmentId)} className="btn-secondary flex-1">Decline</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContractorDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = () => {
    api.get('/dashboard/contractor').then(setData).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleAccept = async (id) => {
    await api.post(`/assignments/${id}/accept`);
    loadData();
  };

  const handleDecline = async (id) => {
    if (!confirm('Are you sure you want to decline this gig?')) return;
    await api.post(`/assignments/${id}/decline`);
    loadData();
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" /></div>;

  const { upcomingGigs, pendingCount, currentPeriod } = data;

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-xl font-bold">Hey {user?.name?.split(' ')[0]}!</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/my/earnings" className="card hover:shadow-md transition-shadow">
          <div className="card-body text-center">
            <div className="flex items-center justify-center gap-1">
              <DollarSign className="w-6 h-6 text-green-600" />
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold mt-1">{formatCurrency(currentPeriod.totalEarnings)}</p>
            <p className="text-xs text-gray-500">This period ({currentPeriod.gigCount} gigs)</p>
            {currentPeriod.pendingEarnings > 0 && (
              <p className="text-xs text-yellow-600 mt-1">+{formatCurrency(currentPeriod.pendingEarnings)} pending</p>
            )}
            <p className="text-xs text-hub-600 mt-1">View all pay →</p>
          </div>
        </Link>
        <div className="card">
          <div className="card-body text-center">
            <Calendar className="w-6 h-6 text-hub-600 mx-auto" />
            <p className="text-2xl font-bold mt-1">{upcomingGigs.length}</p>
            <p className="text-xs text-gray-500">Upcoming gigs</p>
          </div>
        </div>
      </div>

      {/* Pending actions */}
      {pendingCount > 0 && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <p className="text-sm text-yellow-800">You have <strong>{pendingCount}</strong> pending gig{pendingCount > 1 ? 's' : ''} to respond to</p>
        </div>
      )}

      {/* Upcoming gigs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Upcoming Gigs This Week</h2>
          <Link to="/my/calendar" className="text-sm text-hub-600">View all</Link>
        </div>
        {upcomingGigs.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-8">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No upcoming gigs</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingGigs.slice(0, 5).map(gig => (
              <GigCard 
                key={gig.assignmentId} 
                gig={gig}
                showActions
                onAccept={handleAccept}
                onDecline={handleDecline}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
